import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import type { CustomerFeatureKey, TenantContext } from '@busbook/shared';
import { ApiError } from '@busbook/ui';
import { api } from './api.js';

/**
 * Boot sequence (docs/04 §1): nothing renders until the tenant context has been
 * fetched, so the app never flashes an unbranded or wrong-branded frame.
 */

export type TenantState =
  | { status: 'loading' }
  | { status: 'ready'; context: TenantContext }
  | { status: 'unknown-host' }
  | { status: 'suspended'; appName: string; primary: string }
  | { status: 'not-live'; appName: string; primary: string }
  | { status: 'offline' };

const Ctx = createContext<TenantContext | null>(null);

export function useTenant(): TenantContext {
  const value = useContext(Ctx);
  if (!value) throw new Error('useTenant must be used inside <TenantGate>');
  return value;
}

/**
 * Whether this operator shows a given customer-app feature.
 *
 * The list arrives already resolved from the API — unknown keys dropped, an unconfigured
 * tenant expanded to every feature on — so there is nothing to reason about here.
 *
 * This hides a control; it does not authorise anything. Every route behind one of these
 * checks the same setting server-side, because a button that is merely absent is still
 * reachable by anyone who reads the network tab.
 */
export function useFeature(key: CustomerFeatureKey): boolean {
  const { branding } = useTenant();
  return branding.customerFeatures.some((f) => f.key === key && f.visible);
}

/** The operator's label for a feature, for buttons that should carry their wording. */
export function useFeatureLabel(key: CustomerFeatureKey, fallback: string): string {
  const { branding } = useTenant();
  return branding.customerFeatures.find((f) => f.key === key)?.label ?? fallback;
}

function maintenanceFrom(error: ApiError): { appName: string; primary: string } {
  const branding = error.extra.branding as
    | { appName?: string; colors?: { primary?: string } }
    | undefined;
  return {
    appName: branding?.appName ?? 'This service',
    primary: branding?.colors?.primary ?? '#2563EB',
  };
}

/** Preview links carry `?preview=<token>`; remember it for the whole session. */
function capturePreviewToken(): string | null {
  const fromUrl = new URLSearchParams(window.location.search).get('preview');
  if (fromUrl) {
    try {
      sessionStorage.setItem('busbook.preview', fromUrl);
    } catch {
      /* storage may be unavailable; the query param still works for this load */
    }
    return fromUrl;
  }
  try {
    return sessionStorage.getItem('busbook.preview');
  } catch {
    return null;
  }
}

export function useTenantBoot(): TenantState {
  const [state, setState] = useState<TenantState>({ status: 'loading' });

  useEffect(() => {
    let cancelled = false;

    void (async () => {
      const preview = capturePreviewToken();
      try {
        const context = await api.request<TenantContext>('/public/tenant/context', {
          method: 'GET',
          auth: false,
          headers: preview ? { 'x-preview-token': preview } : undefined,
        });
        if (!cancelled) setState({ status: 'ready', context });
      } catch (error) {
        if (cancelled) return;
        if (error instanceof ApiError) {
          if (error.code === 'TENANT_SUSPENDED') {
            setState({ status: 'suspended', ...maintenanceFrom(error) });
            return;
          }
          if (error.code === 'TENANT_NOT_LIVE') {
            setState({ status: 'not-live', ...maintenanceFrom(error) });
            return;
          }
          setState({ status: 'unknown-host' });
          return;
        }
        setState({ status: 'offline' });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  return state;
}

export function TenantProvider({
  context,
  children,
}: {
  context: TenantContext;
  children: ReactNode;
}) {
  return <Ctx.Provider value={context}>{children}</Ctx.Provider>;
}
